<template>
  <div class="subida-documentos">
    <b-card title="Subir Documentos" class="mb-4">
      <b-form @submit.prevent="subirDocumento" v-if="!subiendo">
        <b-row>
          <b-col md="6">
            <b-form-group label="Archivo" label-for="archivo">
              <b-form-file
                id="archivo"
                v-model="archivoSeleccionado"
                :state="archivoSeleccionado ? null : false"
                placeholder="Selecciona un archivo..."
                accept="*/*"
                @change="onFileSelected"
                required
              />
              <b-form-invalid-feedback v-if="!archivoSeleccionado">
                Por favor selecciona un archivo
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Categoría" label-for="categoria">
              <b-form-select
                id="categoria"
                v-model="documentoForm.idCategoriaArchivo"
                :options="categoriasOptions"
                :state="documentoForm.idCategoriaArchivo ? null : false"
                required
              />
              <b-form-invalid-feedback v-if="!documentoForm.idCategoriaArchivo">
                Por favor selecciona una categoría
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label="Versión" label-for="version">
              <b-form-input
                id="version"
                v-model="documentoForm.version"
                placeholder="Ej: 1.0"
                :state="documentoForm.version ? null : false"
                required
              />
              <b-form-invalid-feedback v-if="!documentoForm.version">
                Por favor ingresa una versión
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Nombre del archivo" label-for="nombreArchivo">
              <b-form-input
                id="nombreArchivo"
                v-model="documentoForm.nombreArchivo"
                :state="documentoForm.nombreArchivo ? null : false"
                required
              />
              <b-form-invalid-feedback v-if="!documentoForm.nombreArchivo">
                Por favor ingresa el nombre del archivo
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>

        <div class="d-flex justify-content-between align-items-center">
          <div class="text-muted small">
            <feather-icon icon="InfoIcon" size="14" class="mr-1" />
            Formatos soportados: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, JPG, PNG, ZIP, RAR
          </div>
          <b-button type="submit" variant="primary" :disabled="!formularioValido">
            <feather-icon icon="UploadIcon" size="16" class="mr-1" />
            Subir Documento
          </b-button>
        </div>
      </b-form>

      <!-- Loading state -->
      <div v-if="subiendo" class="text-center py-4">
        <b-spinner class="text-primary"></b-spinner>
        <p class="mt-2">Subiendo documento...</p>
      </div>
    </b-card>
  </div>
</template>

<script>
import { DocumentoService } from '@/services/documentoService'
import { CategoriaArchivoService } from '@/services/categoriaArchivoService'

export default {
  name: 'SubidaDocumentos',
  props: {
    idActividad: {
      type: Number,
      required: false
    },
    idUsuario: {
      type: Number,
      required: true
    },
    modoTemporal: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      archivoSeleccionado: null,
      subiendo: false,
      documentoForm: {
        idActividad: this.idActividad,
        nombreArchivo: '',
        nombreArchivoOriginal: '',
        idCategoriaArchivo: null,
        version: '',
        idSubidoPor: this.idUsuario,
        contenido: ''
      },
      categorias: [],
      categoriasOptions: []
    }
  },
  computed: {
    formularioValido() {
      return this.archivoSeleccionado && 
             this.documentoForm.nombreArchivo && 
             this.documentoForm.idCategoriaArchivo && 
             this.documentoForm.version
    }
  },
  async mounted() {
    await this.cargarCategorias()
  },
  methods: {
    async cargarCategorias() {
      try {
        this.categorias = await CategoriaArchivoService.getAll()
        this.categoriasOptions = this.categorias.map(cat => ({
          value: cat.idCategoriaArchivo,
          text: cat.nombre
        }))
        this.categoriasOptions.unshift({ value: null, text: 'Selecciona una categoría' })
      } catch (error) {
        console.error('Error al cargar categorías:', error)
        this.$toast({
          message: 'Error al cargar las categorías',
          variant: 'danger',
        })
      }
    },
    onFileSelected(event) {
      const file = event.target.files[0]
      if (file) {
        // Validar tamaño del archivo (máximo 10MB)
        const maxSize = 10 * 1024 * 1024 // 10MB en bytes
        if (file.size > maxSize) {
          this.$toast({
            message: 'El archivo es demasiado grande. Tamaño máximo: 10MB',
            variant: 'warning',
          })
          // Limpiar el input
          event.target.value = ''
          return
        }

        // Guardar el nombre original del archivo
        this.documentoForm.nombreArchivoOriginal = file.name
        // El nombre del archivo puede ser editado por el usuario
        this.documentoForm.nombreArchivo = file.name
        this.archivoSeleccionado = file

        // Mostrar información del archivo
        this.$toast({
          message: `Archivo seleccionado: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`,
          variant: 'info',
        })
      }
    },
    async subirDocumento() {
      if (!this.formularioValido) {
        this.$toast({
          message: 'Por favor completa todos los campos',
          variant: 'warning',
        })
        return
      }

      this.subiendo = true
      try {
        // Convertir archivo a base64
        const contenidoBase64 = await DocumentoService.fileToBase64(this.archivoSeleccionado)
        
        // Preparar datos del documento
        const documentoData = {
          ...this.documentoForm,
          contenido: contenidoBase64,
          tamaño: this.archivoSeleccionado.size,
          nombreCategoria: this.categorias.find(cat => cat.idCategoriaArchivo === this.documentoForm.idCategoriaArchivo)?.nombre || ''
        }

        if (this.modoTemporal) {
          // En modo temporal, solo emitir los datos sin subir al servidor
          this.$emit('documento-subido', documentoData)
          this.$toast({
            message: 'Documento preparado correctamente',
            variant: 'success',
          })
          this.limpiarFormulario()
        } else {
          // Modo normal: subir al servidor
          const documentoCreado = await DocumentoService.upload(documentoData)

          this.$toast({
            message: 'Documento subido correctamente',
            variant: 'success',
          })

          // Emitir evento para notificar al componente padre con el documento creado
          this.$emit('documento-subido', documentoCreado)

          // Limpiar formulario
          this.limpiarFormulario()

          // Emitir evento para finalizar y cerrar el modal
          this.$emit('finalizar-subida')
        }

      } catch (error) {
        console.error('Error al subir documento:', error)
        this.$toast({
          message: 'Error al subir el documento',
          variant: 'danger',
        })
      } finally {
        this.subiendo = false
      }
    },
    limpiarFormulario() {
      this.archivoSeleccionado = null
      this.documentoForm = {
        idActividad: this.idActividad,
        nombreArchivo: '',
        nombreArchivoOriginal: '',
        idCategoriaArchivo: null,
        version: '',
        idSubidoPor: this.idUsuario,
        contenido: ''
      }
      // Limpiar el input de archivo
      const fileInput = document.getElementById('archivo')
      if (fileInput) {
        fileInput.value = ''
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.subida-documentos {
  .form-group {
    margin-bottom: 1rem;
  }
}
</style>
